import { type KeyboardEvent, useCallback, useContext, useRef, useState } from 'react';
import { Button, Input, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { Add24Regular, Checkmark24Regular, Dismiss24Regular } from '@fluentui/react-icons';
import type { BubbleMenuProps } from '@tiptap/react/menus';
import DocumentContext from '@modules/documents/contexts/DocumentContext.context';
import findNodeByFieldId from '../../utils/findNodeByFieldId';
import useDropdownOptions from '../../../hooks/useDropdownOptions.hook';
import useEditorActive from '../../../hooks/useEditorActive.hook';
import NodeBubbleMenuShell from '../../../components/NodeBubbleMenuShell';
import { TEMPLATE_FIELD_DROPDOWN_NAME } from '../../../doc-engine/templateFields';
import { usesFormFields } from '../../../helpers/formFields.helper';
import { findSelectedField } from '../formField/formFieldHelpers';
import DropdownChoicesPanel, { TDropdownChoices } from './DropdownChoicesPanel';
import { MAX_DROPDOWN_OPTION_LENGTH, normalizeDropdownOption, setDropdownOptions } from './dropdownFieldUtils';
import { handleSettingsHistoryKeys } from './fieldSettings';
import FieldSettingsControls from './FieldSettingsControls';

const useStyles = makeStyles({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    width: '280px',
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  optionLabel: {
    flex: 1,
    fontSize: tokens.fontSizeBase300,
    cursor: 'pointer',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  optionLabelNonInteractive: {
    cursor: 'default',
  },
  optionLabelSelected: {
    fontWeight: tokens.fontWeightSemibold,
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  addRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  addInput: {
    flex: 1,
  },
  emptyState: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    padding: tokens.spacingVerticalS,
  },
  clearLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

// --- Template mode: configure options ---

type TConfigPanelProps = {
  fieldId: string;
  options: string[];
  selectedValue: string;
  onOptionsChange: (options: string[]) => void;
  onSelectedValueChange: (value: string) => void;
};

const DropdownConfigPanel = ({
  fieldId,
  options,
  selectedValue,
  onOptionsChange,
  onSelectedValueChange,
}: TConfigPanelProps) => {
  const classes = useStyles();
  const [newOption, setNewOption] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAdd = useCallback(() => {
    const trimmed = normalizeDropdownOption(newOption);
    if (!trimmed || options.includes(trimmed)) return;
    onOptionsChange([...options, trimmed]);
    setNewOption('');
    inputRef.current?.focus();
  }, [newOption, options, onOptionsChange]);

  const handleRemove = useCallback(
    (index: number) => {
      if (options.length <= 1) return; // at least one option must remain - an empty list would leave the dropdown unusable
      const updated = options.filter((_, i) => i !== index);
      onOptionsChange(updated);
      // If removed option was selected, select first
      if (options[index] === selectedValue) {
        onSelectedValueChange(updated[0]);
      }
    },
    [options, selectedValue, onOptionsChange, onSelectedValueChange]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAdd();
      }
    },
    [handleAdd]
  );

  return (
    <div className={classes.panel}>
      {selectedValue && (
        <div className={classes.optionRow}>
          <span
            className={mergeClasses(classes.optionLabel, classes.clearLabel)}
            onClick={() => onSelectedValueChange('')}
          >
            Clear selection
          </span>
        </div>
      )}
      {options.map((opt, i) => (
        <div key={`${fieldId}-${opt}`} className={classes.optionRow}>
          <span
            className={mergeClasses(classes.optionLabel, opt === selectedValue && classes.optionLabelSelected)}
            onClick={() => onSelectedValueChange(opt)}
            title={opt}
          >
            {opt}
          </span>
          <Button
            size="small"
            appearance="subtle"
            icon={<Dismiss24Regular />}
            disabled={options.length <= 1}
            onClick={() => handleRemove(i)}
          />
        </div>
      ))}
      <div className={classes.addRow}>
        <Input
          ref={inputRef}
          size="small"
          placeholder="New option..."
          value={newOption}
          maxLength={MAX_DROPDOWN_OPTION_LENGTH}
          onChange={(_, data) => setNewOption(data.value)}
          onKeyDown={handleKeyDown}
          className={classes.addInput}
        />
        <Button
          size="small"
          appearance="subtle"
          icon={<Add24Regular />}
          onClick={handleAdd}
          disabled={!newOption.trim()}
        />
      </div>
    </div>
  );
};

// --- Bound/Detached mode: select value ---

type TSelectPanelProps = {
  options: string[];
  selectedValue: string;
  onSelect: (value: string) => void;
  readOnly?: boolean;
};

const DropdownSelectPanel = ({ options, selectedValue, onSelect, readOnly }: TSelectPanelProps) => {
  const classes = useStyles();

  if (!options.length) {
    return (
      <div className={classes.panel}>
        <span className={classes.emptyState}>No options available</span>
        <span
          className={mergeClasses(classes.optionLabel, classes.optionLabelNonInteractive, classes.optionLabelSelected)}
          title={selectedValue}
        >
          {selectedValue}
        </span>
      </div>
    );
  }

  return (
    <div className={classes.panel}>
      {!readOnly && selectedValue && (
        <div className={classes.optionRow}>
          <span className={mergeClasses(classes.optionLabel, classes.clearLabel)} onClick={() => onSelect('')}>
            Clear selection
          </span>
        </div>
      )}
      {options.map((opt, i) => (
        <div key={`${opt}-${i}`} className={classes.optionRow}>
          <span
            className={mergeClasses(
              classes.optionLabel,
              opt === selectedValue && classes.optionLabelSelected,
              readOnly && classes.optionLabelNonInteractive
            )}
            onClick={readOnly ? undefined : () => onSelect(opt)}
            title={opt}
          >
            {opt}
          </span>
          {opt === selectedValue && <Checkmark24Regular />}
        </div>
      ))}
    </div>
  );
};

// --- Main BubbleMenu ---

// A Dropdown in a card has its choices in the card while editing; reading the form still opens the list.
const isOutsideCard: NonNullable<BubbleMenuProps['shouldShow']> = ({ state }) =>
  !!findSelectedField(state, TEMPLATE_FIELD_DROPDOWN_NAME);

const DropdownFieldBubbleMenu = () => {
  const { editor, isEdit, doc, templateDropdownOptionsRef } = useContext(DocumentContext);

  const attrs = useEditorActive((ed) => ed.getAttributes(TEMPLATE_FIELD_DROPDOWN_NAME));
  const fieldId: string | undefined = attrs?.fieldId;
  const selectedValue: string = attrs?.selectedValue || '';
  const options = useDropdownOptions(fieldId) ?? [];

  const handleSelectedValueChange = useCallback(
    (value: string) => {
      if (!editor || !fieldId) return;
      const found = findNodeByFieldId(editor, fieldId);
      if (!found) return;
      editor
        .chain()
        .focus()
        .command(({ tr }) => {
          tr.setNodeAttribute(found.pos, 'selectedValue', value);
          return true;
        })
        .run();
    },
    [editor, fieldId]
  );

  const handleOptionsChange = useCallback(
    (newOptions: string[]) => {
      if (!editor || !fieldId) return;
      templateDropdownOptionsRef.current[fieldId] = newOptions;
      // Options live outside ProseMirror (in a React ref), so auto-save doesn't know about
      // the change. Dispatch a no-op AttrStep (re-setting selectedValue to itself) to create
      // a real doc change that triggers auto-save. addToHistory=false keeps undo clean.
      const found = findNodeByFieldId(editor, fieldId);
      if (!found) return;
      editor.view.dispatch(
        editor.state.tr
          .setNodeAttribute(found.pos, 'selectedValue', found.node.attrs.selectedValue)
          .setMeta('addToHistory', false)
      );
    },
    [editor, fieldId, templateDropdownOptionsRef]
  );

  // A form field's options and answer change together, in one undo step.
  const handleChoicesChange = (choices: TDropdownChoices) => {
    if (!fieldId) return;
    setDropdownOptions(editor, templateDropdownOptionsRef, fieldId, choices.options, choices.selectedValue);
  };

  const configPanel = usesFormFields(doc) ? (
    <div className="flex w-64 flex-col gap-3" onKeyDown={(event) => handleSettingsHistoryKeys(editor, event)}>
      <FieldSettingsControls nodeName={TEMPLATE_FIELD_DROPDOWN_NAME} />
      <DropdownChoicesPanel options={options} selectedValue={selectedValue} onChange={handleChoicesChange} />
    </div>
  ) : (
    <DropdownConfigPanel
      fieldId={fieldId ?? ''}
      options={options}
      selectedValue={selectedValue}
      onOptionsChange={handleOptionsChange}
      onSelectedValueChange={handleSelectedValueChange}
    />
  );

  return (
    <NodeBubbleMenuShell
      nodeName={TEMPLATE_FIELD_DROPDOWN_NAME}
      editor={editor}
      isEdit={true /* show in view mode too */}
      extraPredicate={usesFormFields(doc) && isEdit ? isOutsideCard : undefined}
      paddingCard="medium"
    >
      {isEdit && doc.isTemplate ? (
        configPanel
      ) : (
        <DropdownSelectPanel
          options={options}
          selectedValue={selectedValue}
          onSelect={handleSelectedValueChange}
          readOnly={!isEdit}
        />
      )}
    </NodeBubbleMenuShell>
  );
};

export default DropdownFieldBubbleMenu;
